import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { beginCartAddRun, endCartAddRun } from '@theme/cart-add-perf';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * Omit `sections` on POST /cart/add so Shopify only mutates the cart (fast).
 * Mini-cart HTML loads afterward via the Section Rendering API.
 */
const USE_SPLIT_CART_ADD = true;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Sets or clears the "adding" state (shows Figma Loading 2 spinner).
   * @param {boolean} adding - Whether the button is in the adding state.
   */
  setAdding(adding) {
    const btn = this.refs.addToCartButton;
    if (adding) {
      btn.classList.add('atc-adding');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.classList.remove('atc-adding');
      btn.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // App blocks in the form (notify-me, etc.) often bind on first paint only; OOS-only PDPs need a hook after connect.
    queueMicrotask(() => this.#maybeAnnounceDomUpdatedForOosOnlyForm());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit(event) {
    // Theme event delegation sets event.target to the component (element with on:submit), not the form.
    // Resolve the actual submitted form via submitter or activeElement so we don't return and allow a full-page submit.
    const delegatedTarget = /** @type {HTMLFormElement | null} */ (event?.target);
    const submitterForm = /** @type {HTMLButtonElement | HTMLInputElement | null} */ (event?.submitter)?.form ?? null;
    let form = submitterForm ?? (delegatedTarget instanceof HTMLFormElement ? delegatedTarget : null);
    if (!form && document.activeElement) {
      const activeForm = document.activeElement.closest?.('form');
      if (activeForm && this.contains(activeForm)) form = activeForm;
    }
    // Only handle the add-to-cart form; let other forms submit normally
    if (!form || !this.contains(form) || form.getAttribute('data-type') !== 'add-to-cart-form') {
      return;
    }

    const submitter = /** @type {HTMLElement | null} */ (event.submitter);
    const hasAddToCartInForm = !!form.querySelector('add-to-cart-component');

    // Out-of-stock UI (e.g. notify-me app block) often lives inside the same product form.
    // Do not hijack those submits into the AJAX add-to-cart flow.
    if (!hasAddToCartInForm) {
      event.preventDefault();
      return;
    }

    if (submitter && !submitter.closest('add-to-cart-component')) {
      event.preventDefault();
      return;
    }

    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Only block when the actual add-to-cart control was clicked while disabled (avoids stale ref after variant morph)
    if (submitter?.closest('add-to-cart-component') && addToCartButtonContainer?.refs.addToCartButton?.disabled) {
      return;
    }

    // Send the add to cart information to the cart
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const currentItemSource = formData.get('properties[_itemSource]');
    if (currentItemSource == null || String(currentItemSource).trim() === '') {
      formData.set('properties[_itemSource]', 'FPC');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    const uniqueCartSectionIds = [...new Set(cartItemComponentsSectionIds)];
    if (!USE_SPLIT_CART_ADD && uniqueCartSectionIds.length) {
      formData.append('sections', uniqueCartSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const delta = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

    beginCartAddRun();

    addToCartButtonContainer?.setAdding(true);
    addToCartButtonContainer?.disable();

    // Prefer JSON when not requesting section HTML on /cart/add (split path).
    const acceptHeader = USE_SPLIT_CART_ADD ? 'application/json' : 'text/html';

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: acceptHeader,
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          // Prefer optimistic count (no extra /cart.js) so the error path stays fast.
          const absoluteItemCount = getOptimisticCartItemCount(delta);
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              action: 'error',
              source: this.dataset.source || 'product-form-component',
              itemCount: absoluteItemCount,
              delta,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          const primarySectionId = uniqueCartSectionIds[0];
          const lineQuantity = getLineQuantityFromCartAddResponse(response, id);
          let itemCount =
            typeof response.item_count === 'number' ? response.item_count : getOptimisticCartItemCount(delta);
          let sections = response.sections;

          // Re-enable the button as soon as the cart mutation succeeds.
          addToCartButtonContainer?.setAdding(false);
          addToCartButtonContainer?.enable();

          if (typeof itemCount === 'number') {
            updateCartBubbleCounts(itemCount);
          }

          const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
          if (quantitySelector?.setCartQuantity && typeof lineQuantity === 'number') {
            quantitySelector.setCartQuantity(lineQuantity);
            this.#updateQuantityLabel(lineQuantity);
          } else if (quantitySelector?.setCartQuantity) {
            // Non-blocking — do not delay drawer open / button unlock for qty label sync.
            void this.#fetchAndUpdateCartQuantity();
          }

          if (USE_SPLIT_CART_ADD && uniqueCartSectionIds.length) {
            // Open drawer + unlock UI immediately; mini-cart HTML loads in the background.
            document.dispatchEvent(
              new CartUpdateEvent({}, this.id, {
                action: 'add',
                source: this.dataset.source || 'product-form-component',
                itemCount,
              })
            );

            const source = this.dataset.source || 'product-form-component';
            const sourceId = this.id;
            void fetchCartSectionsAfterAdd(uniqueCartSectionIds).then((loadedSections) => {
              const sectionCount = getCartItemCountFromSections(loadedSections, primarySectionId);
              const nextCount = typeof sectionCount === 'number' ? sectionCount : itemCount;
              if (typeof nextCount === 'number') {
                updateCartBubbleCounts(nextCount);
              }

              document.dispatchEvent(
                new CartUpdateEvent({}, sourceId, {
                  action: 'add',
                  source,
                  sections: loadedSections,
                  itemCount: nextCount,
                })
              );
            });
          } else {
            if (itemCount == null) {
              itemCount = getCartItemCountFromSections(sections, primarySectionId);
            }

            document.dispatchEvent(
              new CartUpdateEvent({}, this.id, {
                action: 'add',
                source: this.dataset.source || 'product-form-component',
                sections,
                itemCount,
              })
            );
          }

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
        addToCartButtonContainer?.setAdding(false);
        addToCartButtonContainer?.enable();
        endCartAddRun();
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { html } = event.detail.data;
    const forms = html.querySelectorAll('product-form-component');
    const newProductForm = Array.from(forms).find((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.dataset.productId !== this.dataset.productId) return false;
      if (this.dataset.sectionId && el.dataset.sectionId) {
        return el.dataset.sectionId === this.dataset.sectionId;
      }

      return true;
    });

    if (!newProductForm) {
      const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[ref="variantId"], input[name="id"]'));
      if (variantIdInput && event.detail.resource?.id) {
        variantIdInput.value = String(event.detail.resource.id);
      }
      await this.#fetchAndUpdateCartQuantity();

      return;
    }

    for (const attr of newProductForm.attributes) {
      this.setAttribute(attr.name, attr.value);
    }

    morph(this, newProductForm);

    await this.#fetchAndUpdateCartQuantity();

    queueMicrotask(() => this.#dispatchProductFormDomUpdated());
  };

  /**
   * When the buy row is out-of-stock only, there is no add-to-cart component; app blocks still need a mount signal.
   */
  #maybeAnnounceDomUpdatedForOosOnlyForm() {
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || form.querySelector('add-to-cart-component')) return;
    this.#dispatchProductFormDomUpdated();
  }

  /**
   * Notifies Theme App Extensions and scripts that product form markup was refreshed (variant morph or OOS-only first paint).
   * @see ThemeEvents.productFormDomUpdated
   */
  #dispatchProductFormDomUpdated() {
    const detail = {
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
      target: this,
    };
    this.dispatchEvent(
      new CustomEvent(ThemeEvents.productFormDomUpdated, { bubbles: true, composed: true, detail: { ...detail } })
    );
    document.dispatchEvent(new CustomEvent(ThemeEvents.productFormDomUpdated, { bubbles: true, detail: { ...detail } }));
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

/**
 * Reads cart item count from a section rendering API HTML payload.
 * @param {Record<string, string> | undefined} sections
 * @param {string | undefined} sectionId
 * @returns {number | undefined}
 */
function getCartItemCountFromSections(sections, sectionId) {
  if (!sections || !sectionId) return undefined;
  const html = sections[sectionId];
  if (!html) return undefined;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const raw = doc.querySelector('[ref="cartItemCount"]')?.textContent;
  if (raw == null || raw === '') return undefined;
  const count = parseInt(raw, 10);
  return Number.isFinite(count) ? count : undefined;
}

/**
 * Optimistic cart item count from session cache + quantity added (avoids /cart.js on hot path).
 * @param {number} delta
 * @returns {number}
 */
function getOptimisticCartItemCount(delta) {
  const qty = Number.isFinite(delta) ? delta : 1;
  try {
    const cached = JSON.parse(sessionStorage.getItem('cart-count') || '{}');
    const cachedValue = parseInt(cached?.value ?? '0', 10);
    return (Number.isFinite(cachedValue) ? cachedValue : 0) + qty;
  } catch (_) {
    return qty;
  }
}

/**
 * @param {number} itemCount
 */
function updateCartBubbleCounts(itemCount) {
  document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
    el.textContent = itemCount < 100 ? String(itemCount) : '';
  });
}

/**
 * @param {any} response
 * @param {FormDataEntryValue | null} variantId
 * @returns {number | undefined}
 */
function getLineQuantityFromCartAddResponse(response, variantId) {
  if (variantId == null) return undefined;
  const vid = String(variantId);

  // JSON body with items[] (Cart AJAX API batch add)
  if (Array.isArray(response?.items)) {
    const line = response.items.find(
      /** @param {any} item */
      (item) => String(item.variant_id ?? item.id) === vid
    );
    return typeof line?.quantity === 'number' ? line.quantity : undefined;
  }

  // Classic FormData /cart/add.js returns a single line item object
  if (String(response?.variant_id ?? response?.id) === vid && typeof response?.quantity === 'number') {
    return response.quantity;
  }

  return undefined;
}

/**
 * Fetch cart section HTML in parallel (Section Rendering API).
 * @param {string[]} sectionIds
 * @returns {Promise<Record<string, string>>}
 */
async function fetchCartSectionsAfterAdd(sectionIds) {
  const entries = await Promise.all(
    sectionIds.map(async (sectionId) => {
      const html = await sectionRenderer.getSectionHTML(sectionId, false);
      return /** @type {[string, string]} */ ([sectionId, html]);
    })
  );
  return Object.fromEntries(entries);
}
